/**
 * 对话框: 遮罩 + 面板, 渲染到 body。
 *  - Esc 关闭; 点遮罩关闭 (closeOnOverlay=false 可关掉)
 *  - 打开时焦点移入 (initialFocusRef 或第一个可聚焦元素), Tab 在面板内循环, 关闭后焦点还给打开前的元素
 *  - 打开期间锁住 body 滚动 (支持嵌套)
 *  - 手机上贴底弹出 (sheet), sm 以上居中
 */
import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  /** 底部按钮区 (右对齐) */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** 打开时聚焦的元素; 默认第一个可聚焦元素 */
  initialFocusRef?: RefObject<HTMLElement>;
  closeOnOverlay?: boolean;
  /** 隐藏右上角关闭按钮 */
  hideClose?: boolean;
  /** 面板额外 class (如全屏看图时去掉内边距) */
  className?: string;
  /** 内容区 class */
  bodyClassName?: string;
  "aria-label"?: string;
  children?: ReactNode;
}

const SIZES = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
  full: "sm:max-w-[min(96vw,1200px)]",
};

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

let lockCount = 0;
let savedOverflow = "";
function lockScroll() {
  if (lockCount++ === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}
function unlockScroll() {
  if (--lockCount <= 0) {
    lockCount = 0;
    document.body.style.overflow = savedOverflow;
  }
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  size = "md",
  initialFocusRef,
  closeOnOverlay = true,
  hideClose = false,
  className,
  bodyClassName,
  "aria-label": ariaLabel,
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    lockScroll();

    const panel = panelRef.current;
    const target =
      initialFocusRef?.current ?? panel?.querySelector<HTMLElement>(`[data-autofocus], ${FOCUSABLE}`) ?? panel;
    target?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.closest("[hidden],[inert]"),
      );
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockScroll();
      // 焦点还给打开前的元素 (它可能已被卸载)
      if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus();
    };
    // initialFocusRef 只在打开时读一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-black/45 animate-in fade-in-0 duration-150"
        onClick={closeOnOverlay ? onClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={cn(
          "relative z-10 flex max-h-[92dvh] w-full flex-col overflow-hidden bg-card text-card-foreground shadow-xl outline-none",
          "rounded-t-2xl pb-[env(safe-area-inset-bottom)] sm:rounded-xl sm:pb-0",
          "animate-in fade-in-0 slide-in-from-bottom-4 duration-200 sm:zoom-in-95 sm:slide-in-from-bottom-0",
          SIZES[size],
          className,
        )}
      >
        {(title || !hideClose) && (
          <div className="flex items-start justify-between gap-3 px-5 pt-4 sm:pt-5">
            <div className="min-w-0 space-y-1">
              {title && (
                <h2 id={titleId} className="text-section text-foreground">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descId} className="text-sm text-muted-foreground">
                  {description}
                </p>
              )}
            </div>
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="关闭"
                className="-mr-1.5 -mt-0.5 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t bg-muted/40 px-5 py-3 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
